
import React, { Component, ErrorInfo } from 'react';
import { toast } from 'sonner';
import ErrorDisplay from './errors/ErrorDisplay';
import { logComponentError, reportError } from '@/utils/errorUtils';

interface FeatureErrorBoundaryProps {
  children: React.ReactNode;
  componentName: string;
  onRetry?: () => void;
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class FeatureErrorBoundary extends Component<FeatureErrorBoundaryProps, FeatureErrorBoundaryState> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): FeatureErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error with enhanced context
    logComponentError(this.props.componentName, error, errorInfo);
    
    // Store errorInfo in state for reporting
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReport = () => {
    const { error, errorInfo } = this.state;
    
    if (error) {
      // Report the error with enhanced context
      reportError(this.props.componentName, error, errorInfo || undefined);
      toast.success('Error report sent. Thank you for helping us improve!');
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          componentName={this.props.componentName}
          error={this.state.error}
          onRetry={this.handleRetry}
          onReport={this.handleReport}
        />
      );
    }

    return this.props.children;
  }
}

export default FeatureErrorBoundary;
